
import { defineComponent, PropType } from "vue";
import ColorPicker from "@caohenghu/vue-colorpicker";
import {
  AvatarItem,
  Color,
  AvatarSelection,
  CustomizableItem,
  CustomizableItemOptions,
} from "./AvatarMaker.types";

export default defineComponent({
  name: "AvatarMaker",
  components: {
    ColorPicker,
  },
  props: {
    options: {
      type: Object as PropType<AvatarItem[]>,
      required: true,
      default: [],
    },
    width: {
      type: Number,
      required: false,
      default: 1200,
    },
    height: {
      type: Number,
      required: false,
      default: 1200,
    },
    title: {
      type: String,
      default: "Avatar Maker",
    },
  },
  data() {
    return {
      color: "",
      canvas: null as CanvasRenderingContext2D | null,
      avatar: [] as AvatarSelection[],
      tab: this.options[0] as AvatarItem | "background",
      variants: this.options[0].items[0] as CustomizableItem | null,
      variant: this.options[0].items[0]
        .options[0] as CustomizableItemOptions | null,
    };
  },
  mounted() {
    const canvas = this.$refs.canvas as HTMLCanvasElement;
    this.canvas = canvas.getContext("2d");
    this.randomize();
  },
  methods: {
    changeBackground(color: Color) {
      const { r, g, b, a } = color.rgba;
      this.color = `rgba(${r}, ${g}, ${b}, ${a})`;
      this.drawImage();
    },
    async drawImage() {
      if (this.canvas) {
        const canvas = this.canvas;
        canvas.fillStyle = this.color;
        canvas.fillRect(0, 0, this.width, this.height);
        let promiseImages: Promise<void>[] = [];
        let images: {
          image: HTMLImageElement;
          variant: AvatarSelection["variant"];
        }[] = [];
        for (const option of this.avatar) {
          promiseImages.push(
            new Promise((resolve) => {
              const image = new Image();
              image.onload = function () {
                resolve();
              };
              image.src = option.variant.url;
              images.push({ image: image, variant: option.variant });
            })
          );
        }
        await Promise.all(promiseImages).then(() => {
          images.forEach((item) => {
            canvas.drawImage(item.image, item.variant.x, item.variant.y);
          });
        });
      }
    },
    randomize() {
      let avatar: AvatarSelection[] = [];
      this.options.forEach((section) => {
        const variants =
          section.items[Math.floor(Math.random() * section.items.length)];
        const variant =
          variants.options[Math.floor(Math.random() * variants.options.length)];
        if (this.tab !== "background" && this.tab.name === section.name) {
          this.variants = variants;
          this.variant = variant;
        }
        avatar.push({
          name: section.name,
          variant: {
            name: variants.name,
            url: variant.url,
            x: variants.x,
            y: variants.y,
          },
        });
      });
      this.avatar = avatar;
      this.color = `#${Math.floor(Math.random() * 16777215).toString(16)}`;
      this.drawImage();
    },
    saveImage() {
      const canvas = this.$refs.canvas as HTMLCanvasElement;
      const dataURL = canvas.toDataURL("image/png");
      const link = this.$refs.saveAvatar as HTMLLinkElement;
      link.href = dataURL;
    },
    setItem(item: CustomizableItem) {
      if (this.tab !== "background") {
        const name = this.tab.name;
        this.variants = item;
        let avatar: AvatarSelection[] = [];
        this.avatar.forEach((section) => {
          if (section.name != name) {
            avatar.push(section);
          } else {
            this.variant = item.options[0];
            avatar.push({
              name: name,
              variant: {
                name: item.name,
                x: item.x,
                y: item.y,
                url: item.options[0].url,
              },
            });
          }
        }, this);
        this.avatar = avatar;
        this.drawImage();
      }
    },
    setVariant(variant: CustomizableItemOptions) {
      this.variant = variant;
      if (this.tab === "background") {
        return;
      }
      let avatar: AvatarSelection[] = [];
      this.avatar.forEach((section) => {
        if (this.tab === "background") {
          return;
        }
        if (section.name != this.tab.name) {
          avatar.push(section);
        } else {
          if (this.variant && this.variants) {
            avatar.push({
              name: this.tab.name,
              variant: {
                name: this.variants.name,
                x: this.variants.x,
                y: this.variants.y,
                url: variant.url,
              },
            });
          }
        }
      }, this);
      this.avatar = avatar;
      this.drawImage();
    },
    openTab(tab: AvatarItem | "background") {
      this.tab = tab;
      if (tab !== "background") {
        const item = this.avatar.find((setting) => setting.name === tab.name);
        if (item) {
          const variants = tab.items.find(
            (options) => options.name === item.variant.name
          );
          if (variants) {
            const variant = variants.options.find(
              (v) => item.variant.url === v.url
            );
            if (variant) {
              this.variant = variant;
              this.variants = variants;
              return;
            }
          }
        }
      }
      this.variants = null;
      this.variant = null;
    },
  },
});
